import { StyledSelect } from '@monorepo/shared/componentsV2/styled/form';
import { isRequired } from '@monorepo/shared/utils/validators';
import { DatasetPropertyResponse, DatasetResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';
import {
  filterConditionProperties,
  getUserFriendlyPropertyLabel,
} from './helpers';

type BaseConditionPickerProps = {
  dataset?: DatasetResponse;
  fieldName: string;
  label: string;
  shouldLabelShow: boolean;
  onChange?: (leftPropertyId: string) => void;
};

function ConditionPicker({
  dataset,
  fieldName,
  filteredSchemaProperties,
  label,
  shouldLabelShow,
  onChange,
}: BaseConditionPickerProps & {
  filteredSchemaProperties: DatasetPropertyResponse[];
}) {
  const getPropertyLabel = useCallback(
    (property) => getUserFriendlyPropertyLabel(property, dataset),
    [dataset],
  );

  const options = useMemo<{ label: string; value: string }[]>(
    () =>
      filteredSchemaProperties
        .map((property) => ({
          label: getPropertyLabel(property),
          value: property.id,
        }))
        .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label)),
    [filteredSchemaProperties, getPropertyLabel],
  );

  const isDisabled = !dataset;

  return (
    <StyledSelect
      isLabelHidden={!shouldLabelShow}
      label={label}
      options={options}
      name={fieldName}
      required
      disabled={isDisabled}
      validate={isRequired}
      onChange={onChange}
    />
  );
}

export function LeftConditionPicker(props: BaseConditionPickerProps) {
  const { dataset, fieldName } = props;
  const {
    input: { value: selectedPropertyId },
  } = useField<string>(fieldName);
  const filteredSchemaProperties = useMemo(
    () =>
      (dataset?.schemaProperties || []).filter((property) =>
        filterConditionProperties(property, { selectedPropertyId }),
      ),
    [dataset, selectedPropertyId],
  );
  return (
    <ConditionPicker
      {...props}
      filteredSchemaProperties={filteredSchemaProperties}
    />
  );
}

export function RightConditionPicker(
  props: BaseConditionPickerProps & {
    selectedLeftProperty: DatasetPropertyResponse | undefined;
  },
) {
  const { dataset, fieldName, selectedLeftProperty } = props;
  const {
    input: { value: selectedPropertyId },
  } = useField<string>(fieldName);
  const filteredSchemaProperties = useMemo(
    () =>
      (dataset?.schemaProperties || []).filter((property) =>
        filterConditionProperties(property, {
          selectedPropertyId,
          leftProperty: selectedLeftProperty,
        }),
      ),
    [dataset, selectedPropertyId, selectedLeftProperty],
  );
  return (
    <ConditionPicker
      {...props}
      filteredSchemaProperties={filteredSchemaProperties}
    />
  );
}
